import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Search, ChevronRight, ChevronLeft, Play, Clock, Award, CheckCircle2,
  AlertTriangle, Calendar, TrendingUp, BookOpen, Video, Scale,
  ClipboardList, Zap, Brain, FileText, ArrowRight, Flame, Mic, Bell, Shield, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import ActivityTimelineChart from '@/components/reports/ActivityTimelineChart';
import OnboardingWidget from '@/components/OnboardingWidget';
import LeaderboardWidget from '@/components/LeaderboardWidget';

const API = process.env.REACT_APP_BACKEND_URL;
const H = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const CATEGORY_LABELS = {
  aml_cft: 'AML / CFT', odeme_sistemleri: 'Ödeme Sistemleri',
  bilgi_guvenligi: 'Bilgi Güvenliği', kvkk_veri: 'KVKK ve Veri Koruma',
  pci_dss: 'PCI DSS', iso_standartlar: 'ISO Standartları',
  tcmb_bddk: 'TCMB / BDDK', yeni_teknoloji: 'Yeni Teknolojiler',
};

const STATUS_CONFIG = {
  completed: { label: 'Tamamlandı', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
  in_progress: { label: 'Devam Ediyor', color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20', icon: Play },
  not_started: { label: 'Başlanmadı', color: 'text-muted-foreground/40', bg: 'bg-muted/30', border: 'border-border', icon: BookOpen },
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [dashData, setDashData] = useState(null);
  const [liveSessions, setLiveSessions] = useState(null);
  const [activeCert, setActiveCert] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [podcastOpen, setPodcastOpen] = useState(false);
  const [podcastError, setPodcastError] = useState(false);
  const [weakTopics, setWeakTopics] = useState([]);
  const [newUpdates, setNewUpdates] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [weakFlipped, setWeakFlipped] = useState({});
  const [weakIdx, setWeakIdx] = useState(0);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    loadData();
    return () => { isMounted.current = false; };
  }, []);

  const loadData = async () => {
    try {
      const [dashRes, modulesRes, sessRes, certRes, weakRes, updatesRes, bulletinsRes] = await Promise.all([
        fetch(`${API}/api/dashboard/summary`, { headers: H() }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
        fetch(`${API}/api/modules`, { headers: H() }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API}/api/live-sessions`, { headers: H() }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API}/api/certificates/my`, { headers: H() }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
        fetch(`${API}/api/user/weak-topics`, { headers: H() }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API}/api/progress/new-updates`, { headers: H() }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API}/api/bulletins`, { headers: H() }).then(r => r.ok ? r.json() : []).catch(() => []),
      ]);
      if (!isMounted.current) return;
      setDashData(dashRes); setModules(modulesRes);
      setLiveSessions(sessRes); setActiveCert(certRes);
      setWeakTopics(Array.isArray(weakRes) ? weakRes : []);
      setNewUpdates(Array.isArray(updatesRes) ? updatesRes : []);
      setBulletins(Array.isArray(bulletinsRes) ? bulletinsRes : []);
    } catch {} finally { if (isMounted.current) setLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const stats = dashData?.user_stats || {};
  const cl = dashData?.continue_learning;
  const continueLesson = cl?.has_continue ? {
    lesson_id: cl.lesson?.id, lesson_name: cl.lesson?.name,
    module_name: cl.lesson?.module_name, progress_percentage: cl.progress_percent || 0,
  } : null;
  const liveSess = (liveSessions || []).filter(s => s.status === 'live');
  const upcomingSess = (liveSessions || []).filter(s => s.status === 'scheduled');

  const getModuleStatus = (m) => {
    if (m.is_completed) return 'completed';
    if (m.progress > 0 || m.correct_answers > 0) return 'in_progress';
    return 'not_started';
  };

  const moduleProgress = dashData?.module_progress || {};
  const enrichedModules = modules.map(m => {
    const mp = moduleProgress[m.id];
    return {
      ...m,
      is_completed: mp ? mp.completed_lessons >= mp.total_lessons : m.is_completed,
      progress: mp ? mp.completion_percentage : (m.progress || 0),
    };
  });

  const completedCount = enrichedModules.filter(m => m.is_completed).length;
  const inProgressCount = enrichedModules.filter(m => getModuleStatus(m) === 'in_progress').length;
  const totalHours = Math.round((stats.total_time_spent || 0) / 3600);
  const overallProgress = modules.length ? Math.round(completedCount / modules.length * 100) : 0;

  const filteredModules = enrichedModules.filter(m => {
    if (search && !m.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'zorunlu' && !m.mandatory) return false;
    if (filter === 'devam' && getModuleStatus(m) !== 'in_progress') return false;
    if (filter === 'tamamlanan' && getModuleStatus(m) !== 'completed') return false;
    return true;
  });

  const grouped = {};
  filteredModules.forEach(m => {
    const cat = m.category || 'diger';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(m);
  });


  // Next lesson to study
  const nextModule = enrichedModules.find(m => getModuleStatus(m) === 'in_progress') || enrichedModules.find(m => getModuleStatus(m) === 'not_started');

  return (
    <div data-testid="student-dashboard" className="page-enter">
      <div className="px-4 sm:px-6 py-6 max-w-[1400px] mx-auto space-y-6">

        {/* Welcome */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-display">Merhaba, {user?.name?.split(' ')[0] || 'Öğrenci'}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{completedCount} modul tamamlandı, {inProgressCount} devam ediyor</p>
          </div>
          <div className="flex items-center gap-3">
            {stats.current_streak > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20">
                <Flame className="w-3.5 h-3.5 text-orange-400" />
                <span className="text-xs font-bold text-orange-400">{stats.current_streak} gun</span>
              </div>
            )}
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
              <Award className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-bold text-amber-400">{stats.total_xp || 0} XP</span>
            </div>
          </div>
        </div>

        {/* Live Session Alert - only when live */}
        {liveSess.length > 0 && (
          <Card className="border-red-500/30 bg-gradient-to-r from-red-500/[0.06] to-transparent cursor-pointer" onClick={() => navigate(`/live/${liveSess[0].id}`)}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center"><div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /></div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-red-400">Simdi Canli</div>
                  <div className="text-sm font-bold">{liveSess[0].title}</div>
                </div>
              </div>
              <Button className="bg-red-500 hover:bg-red-600 text-white" size="sm">Katil<ArrowRight className="w-3 h-3 ml-1" /></Button>
            </CardContent>
          </Card>
        )}

        {/* Main: Continue Learning */}
        {continueLesson ? (
          <Card className="card-interactive cursor-pointer border-primary/20" onClick={() => navigate(`/lesson/${continueLesson.lesson_id || continueLesson.id}`)}>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                  <Play className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-primary/50">Kaldığın Yerden Devam Et</div>
                  <div className="text-base font-bold">{continueLesson.lesson_name || continueLesson.name}</div>
                </div>
                <ArrowRight className="w-5 h-5 text-primary ml-auto shrink-0" />
              </div>
              <div className="flex items-center gap-3">
                <Progress value={continueLesson.progress_percentage || 0} className="h-2 flex-1" />
                <span className="text-xs font-bold text-primary shrink-0">%{continueLesson.progress_percentage || 0}</span>
              </div>
            </CardContent>
          </Card>
        ) : nextModule ? (
          <Card className="card-interactive cursor-pointer border-primary/20" onClick={() => navigate(`/module/${nextModule.id}`)}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center"><BookOpen className="w-5 h-5 text-primary" /></div>
              <div className="flex-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-primary/50">Sıradaki Modül</div>
                <div className="text-base font-bold">{nextModule.name}</div>
                <div className="text-xs text-muted-foreground">{nextModule.topic_count || 0} ders</div>
              </div>
              <Button size="sm">Basla<ArrowRight className="w-3 h-3 ml-1" /></Button>
            </CardContent>
          </Card>
        ) : (
          <Card><CardContent className="p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-lg font-bold">Tum egitimler tamamlandı!</h3>
            <p className="text-sm text-muted-foreground mt-1">Sertifika almak için sınavlara katılabilirsiniz</p>
          </CardContent></Card>
        )}

        {/* KATMAN 1 — Acil uyarı: Okunmamış zorunlu bülten varsa en üstte banner */}
        {bulletins.filter(b => !b.is_read).length > 0 && (
          <Card
            className="border-amber-500/30 bg-gradient-to-r from-amber-500/[0.08] to-transparent cursor-pointer"
            onClick={() => {
              const first = bulletins.find(b => !b.is_read);
              if (first) navigate(`/lesson/${first.id}`);
            }}
            data-testid="unread-bulletins-alert"
          >
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                  <Bell className="w-5 h-5 text-amber-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400">
                    Okunmamış Uyum Duyurusu
                  </div>
                  <div className="text-sm font-bold truncate">
                    {bulletins.filter(b => !b.is_read).length} duyuru bekliyor
                  </div>
                </div>
              </div>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white shrink-0" size="sm">
                Hemen Oku<ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* KATMAN 3 — Öncelikli Bilgi: Son Güncellemeler (7) + Hatırlatma (5) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

          {/* Weak Topic - question/answer */}
          <Card className="border-amber-500/10 lg:col-span-5 lg:order-2">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-bold">Hatırlatma</span>
                {weakTopics.filter(w => !w.is_reviewed).length > 0 && <Badge className="bg-amber-500/10 text-amber-400 border-0 text-[9px] ml-auto">{weakTopics.filter(w => !w.is_reviewed).length}</Badge>}
              </div>
              {weakTopics.filter(w => !w.is_reviewed).length > 0 ? (() => {
                const unreviewed = weakTopics.filter(w => !w.is_reviewed);
                const idx = Math.min(weakIdx, unreviewed.length - 1);
                const c = unreviewed[idx]; const q = c.question;
                return (
                  <div>
                    <div className="cursor-pointer" onClick={() => setWeakFlipped(p => ({...p, [c.id]: !p[c.id]}))}>
                      <div className={`p-4 rounded-xl border min-h-[80px] flex flex-col items-center justify-center text-center transition-all ${weakFlipped[c.id] ? 'border-emerald-500/20 bg-emerald-500/[0.03]' : 'border-amber-500/15 bg-amber-500/[0.02]'}`}>
                        {!weakFlipped[c.id] ? (
                          <><p className="text-sm text-foreground/80">{q ? q.question_text : c.law_id}</p><p className="text-[10px] text-muted-foreground/30 mt-2">Cevap için tıkla</p></>
                        ) : (
                          <><p className="text-sm font-medium text-emerald-400">{q ? `${q.correct_letter}) ${q.correct_answer}` : c.regulation_text}</p>
                          {q?.explanation && <p className="text-xs text-foreground/50 mt-1">{q.explanation.slice(0, 120)}</p>}
                          <Button size="sm" variant="ghost" className="text-[10px] text-emerald-400 mt-2 h-7" onClick={e => {e.stopPropagation(); fetch(`${API}/api/user/weak-topics/${c.id}/review`, {method:'POST',headers:H()}).then(() => {setWeakTopics(p => p.map(w => w.id === c.id ? {...w, is_reviewed: true} : w)); toast.success('Anladım!')})}}>Anladım</Button></>
                        )}
                      </div>
                    </div>
                    {unreviewed.length > 1 && (
                      <div className="flex items-center justify-center gap-3 mt-2">
                        <button onClick={() => setWeakIdx(Math.max(0, idx - 1))} disabled={idx === 0}
                          className="w-7 h-7 rounded-lg border border-border/50 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors">
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-[10px] text-muted-foreground">{idx + 1} / {unreviewed.length}</span>
                        <button onClick={() => setWeakIdx(Math.min(unreviewed.length - 1, idx + 1))} disabled={idx >= unreviewed.length - 1}
                          className="w-7 h-7 rounded-lg border border-border/50 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors">
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })() : (() => {
                const unreadBulletins = bulletins.filter(b => !b.is_read);
                if (unreadBulletins.length > 0) {
                  const b = unreadBulletins[0];
                  return (
                    <div className="p-4 rounded-xl border border-blue-500/15 bg-blue-500/[0.03]">
                      <div className="flex items-center gap-2 mb-2">
                        <Bell className="w-3.5 h-3.5 text-blue-400" />
                        <span className="text-[10px] uppercase tracking-wider text-blue-400/80">
                          {unreadBulletins.length} okunmamış uyum duyurusu
                        </span>
                      </div>
                      <p className="text-sm font-medium mb-2 line-clamp-2">{b.title}</p>
                      <Button size="sm" variant="ghost" className="text-[10px] text-blue-400 h-7 p-0" onClick={() => navigate(`/lesson/${b.id}`)}>
                        Şimdi oku →
                      </Button>
                    </div>
                  );
                }
                return <div className="text-sm text-emerald-400/70 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Eksik konu yok, harika!</div>;
              })()}
            </CardContent>
          </Card>

          {/* Son Güncellemeler: Uyum Duyuruları + yüklenen mevzuat dokümanları.
              İki kaynak birleşik, tarihe göre sıralı. Bülten → /lesson/{id},
              mevzuat dokümanı → /mevzuat portal (kaynağı görür). */}
          <Card className="border-border/50 lg:col-span-7 lg:order-1">
            <CardContent className="p-5">
              {(() => {
                // İki kaynağı tek listede birleştir, en yeni 3
                const merged = [
                  ...bulletins.map(b => ({
                    kind: 'bulletin',
                    id: b.id,
                    title: b.title,
                    subtitle: b.source_reference,
                    date: b.created_at,
                    read: b.is_read,
                    isInternal: b.source_type === 'internal_policy',
                  })),
                  ...newUpdates.map(u => ({
                    kind: 'mevzuat',
                    id: `mevz-${u.doc_name}`,
                    title: u.doc_name,
                    subtitle: u.category ? String(u.category).toUpperCase() : null,
                    date: u.published_date,
                    read: false,
                  })),
                ].sort((a, b) => (new Date(b.date || 0)) - (new Date(a.date || 0))).slice(0, 3);

                const unreadCount = bulletins.filter(b => !b.is_read).length + newUpdates.length;

                return (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-bold">Son Güncellemeler</span>
                        {unreadCount > 0 && (
                          <Badge className="bg-amber-500/10 text-amber-400 border-0 text-[9px]">
                            {unreadCount} yeni
                          </Badge>
                        )}
                      </div>
                      <Button size="sm" variant="ghost" className="text-xs text-muted-foreground h-7" onClick={() => navigate('/egitimler')}>Tümünü Gör</Button>
                    </div>
                    {merged.length > 0 ? (
                      <div className="space-y-2.5">
                        {merged.map((item) => {
                          const isBulletin = item.kind === 'bulletin';
                          const SrcIcon = isBulletin ? (item.isInternal ? Building2 : Shield) : Scale;
                          const srcColor = isBulletin
                            ? (item.isInternal ? 'text-blue-400' : 'text-red-400')
                            : 'text-amber-400';
                          const tag = isBulletin ? 'Duyuru' : 'Mevzuat';
                          const href = isBulletin ? `/lesson/${item.id}` : '/mevzuat';
                          return (
                            <div key={item.id} className={`flex items-start gap-2.5 cursor-pointer hover:bg-muted/20 p-1.5 -mx-1.5 rounded-lg transition-colors ${item.read ? 'opacity-60' : ''}`} onClick={() => navigate(href)}>
                              <SrcIcon className={`w-3.5 h-3.5 ${srcColor} shrink-0 mt-0.5`} />
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium truncate">{item.title}</div>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className={`text-[9px] px-1 rounded-sm ${isBulletin ? 'bg-amber-500/10 text-amber-400/80' : 'bg-amber-500/10 text-amber-400/80'}`}>{tag}</span>
                                  {item.subtitle && <span className="text-[9px] text-muted-foreground/70 font-mono truncate">{item.subtitle}</span>}
                                  {item.read && <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400 shrink-0" />}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground/50">Yeni güncelleme yok</div>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>

        </div>

        {/* KATMAN 4 — Yardımcı: Canlı Dersler + Podcast */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Live Sessions */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Video className="w-4 h-4 text-blue-400" /><span className="text-sm font-bold">Canlı Dersler</span></div>
                <Button size="sm" variant="ghost" className="text-xs text-muted-foreground h-7" onClick={() => navigate('/live-sessions')}>Tümü</Button>
              </div>
              {upcomingSess.length > 0 ? (
                <div className="space-y-2">
                  {upcomingSess.slice(0, 2).map((s, i) => (
                    <div key={i} className="flex items-center gap-2.5 p-2 rounded-lg bg-muted/20">
                      <Clock className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                      <div className="min-w-0"><div className="text-xs font-medium truncate">{s.title}</div><div className="text-[10px] text-muted-foreground">{s.scheduled_at ? new Date(s.scheduled_at).toLocaleDateString('tr-TR', {day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}) : ''}</div></div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-sm text-muted-foreground/50">Planlanmış ders yok</div>}
            </CardContent>
          </Card>

          {/* Podcast */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3"><Mic className="w-4 h-4 text-red-400" /><span className="text-sm font-bold">Podcast Dinle</span></div>
              {dashData?.random_podcast?.audio_url && !podcastError ? (() => {
                const pc = dashData.random_podcast;
                const src = pc.audio_url.startsWith('/') ? `${API}${pc.audio_url}` : pc.audio_url;
                return !podcastOpen ? (
                  <div>
                    <p className="text-xs text-foreground/80 truncate mb-2">{pc.lesson_name}</p>
                    <Button size="sm" variant="outline" className="w-full text-xs border-red-500/20 text-red-400"
                      onClick={() => setPodcastOpen(true)}>
                      <Play className="w-3 h-3 mr-1.5" />Dinle
                    </Button>
                  </div>
                ) : (
                  <audio controls autoPlay className="w-full"
                    onError={() => { setPodcastOpen(false); setPodcastError(true); }}
                    onEnded={() => {
                      fetch(`${API}/api/progress/podcast-complete/${pc.lesson_id}`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
                      }).then(() => toast.success('Ders tamamlandı!')).catch(() => {});
                    }}>
                    <source src={src} onError={() => { setPodcastOpen(false); setPodcastError(true); }} />
                  </audio>
                );
              })() : (
                <div className="flex flex-col items-center justify-center py-3 gap-1.5">
                  <Mic className="w-5 h-5 text-muted-foreground/30" />
                  <p className="text-xs text-muted-foreground/50 text-center">
                    Podcast henüz yüklenmedi
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* KATMAN 5 — Motivasyon & Retrospektif: onboarding (varsa) + leaderboard + timeline */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <OnboardingWidget />
          <LeaderboardWidget />
          <ActivityTimelineChart days={30} />
        </div>

        {/* All Trainings Link */}
        <Card className="card-interactive cursor-pointer" onClick={() => navigate('/egitimler')}>
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><BookOpen className="w-5 h-5 text-primary" /></div>
              <div><div className="text-sm font-bold">Tüm Eğitimler</div><div className="text-xs text-muted-foreground">{modules.length} modül, {modules.reduce((s,m) => s + (m.topic_count||0), 0)} ders</div></div>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground" />
          </CardContent>
        </Card>

        {/* Overall Progress Bar */}
        <div className="flex items-center gap-4 px-1">
          <Progress value={overallProgress} className="h-2 flex-1" />
          <span className="text-xs font-bold text-muted-foreground shrink-0">%{overallProgress} tamamlandı</span>
        </div>
      </div>
    </div>
  );
}
